import type { ColumnFiltersState } from '@tanstack/react-table';
import { endOfDay, isValid, startOfDay } from 'date-fns';
import {
	AuditAction,
	BankAccountStatus,
	DealEventCategory,
	DealEventSource,
	DealEventStatus,
	DocumentWorkflowEventLogType,
	InvestmentWorkflowEventType,
	KycEventLogAction,
	VerificationResult,
} from '@dealstack/shared/lib/backendClient';
import { dealEventTypeFor } from '@dealstack/shared/lib/common/business/dealEvent';
import {
	DealEventLogQuery,
	DealEventLogTab,
} from '@dealstack/frontend/app/pages/Project/DealEventLog/DealEventLog.types.ts';

export const DEAL_EVENT_LOG_TAB_CATEGORIES: Record<
	DealEventLogTab,
	DealEventCategory[] | undefined
> = {
	[DealEventLogTab.ALL_EVENTS]: undefined,
	[DealEventLogTab.INVESTOR_ACTIVITY]: [DealEventCategory.PARTICIPANT],
	[DealEventLogTab.CAPITAL_EVENTS]: [DealEventCategory.CAPITAL],
	[DealEventLogTab.TEAM_AND_ACCESS]: [DealEventCategory.ACCESS, DealEventCategory.AUTH],
	[DealEventLogTab.MESSAGING]: [DealEventCategory.MESSAGE],
};

export enum DealEventLogColumn {
	DESCRIPTION = 'description',
	TYPE = 'type',
	ACTOR = 'actor',
	ENTITY = 'entity',
	STATUS = 'status',
	OCCURRED_AT = 'occurredAt',
}

const readFilter = (filters: ColumnFiltersState, id: DealEventLogColumn): unknown =>
	filters.find((filter) => filter.id === id)?.value;

const asStrings = (value: unknown): string[] | undefined => {
	if (!Array.isArray(value)) {
		return undefined;
	}

	const values = value.filter((entry): entry is string => typeof entry === 'string' && !!entry);

	return values.length ? values : undefined;
};

const asText = (value: unknown): string | undefined =>
	typeof value === 'string' && value.trim() ? value.trim() : undefined;

export const readTextFilter = (
	filters: ColumnFiltersState,
	column: DealEventLogColumn
): string | undefined => asText(readFilter(filters, column));

const asOne = (value: unknown): string[] | undefined => {
	const text = asText(value);

	return text ? [text] : undefined;
};

const asOneOrFirst = (value: unknown): string[] | undefined =>
	asOne(value) ?? asStrings(value)?.slice(0, 1);

/** One participant can hold several investments, so their option carries all of their ids. */
export const PARTICIPANT_VALUE_SEPARATOR = ',';

const asParticipantIds = (value: unknown): string[] | undefined =>
	asText(value)?.split(PARTICIPANT_VALUE_SEPARATOR).filter(Boolean);

const asEnums = <T extends string>(allowed: T[], value: unknown): T[] | undefined => {
	const values = asStrings(value)?.filter((entry): entry is T =>
		allowed.some((candidate) => candidate === entry)
	);

	return values?.length ? values : undefined;
};

// The URL hook restores a range as Dates, but a hand-edited param arrives as strings.
const asDate = (value: unknown): Date | undefined => {
	if (value instanceof Date) {
		return isValid(value) ? value : undefined;
	}

	if (typeof value !== 'string') {
		return undefined;
	}

	const parsed = new Date(value);

	return isValid(parsed) ? parsed : undefined;
};

const toDateRange = (value: unknown): { from?: string; to?: string } => {
	if (!Array.isArray(value)) {
		return {};
	}

	const from = asDate(value[0]);
	const to = asDate(value[1]);

	return {
		from: from && startOfDay(from).toISOString(),
		to: to && endOfDay(to).toISOString(),
	};
};

/**
 * The detail each participant source appends to its name, so `KYC` expands to every `KYC_*` the
 * feed can report. The feed composes a type as `${source}_${detail}`, and the Type column shows
 * the source, so the filter offers the source rather than ~70 individual types.
 */
export const PARTICIPANT_TYPE_DETAILS: Readonly<Record<string, readonly string[]>> = {
	[DealEventSource.INVESTMENT_WORKFLOW]: Object.values(InvestmentWorkflowEventType),
	[DealEventSource.KYC]: Object.values(KycEventLogAction),
	[DealEventSource.DOCUMENT_WORKFLOW]: Object.values(DocumentWorkflowEventLogType),
	[DealEventSource.BANK_ACCOUNT_VERIFICATION]: Object.values(VerificationResult),
	[DealEventSource.BANK_ACCOUNT_STATE]: Object.values(BankAccountStatus),
};

/** Every `${TABLE}_${ACTION}` the feed can report for an audited table. */
const auditedTypesOf = (table: string): string[] =>
	Object.values(AuditAction).map((action) => dealEventTypeFor(table, action));

const toTypes = (tab: DealEventLogTab, value: unknown): string[] | undefined => {
	if (tab === DealEventLogTab.TEAM_AND_ACCESS) {
		return asStrings(value);
	}

	// An option is either a participant source, which expands to its own details, or an audited
	// table, which expands to its four actions.
	if (tab === DealEventLogTab.INVESTOR_ACTIVITY) {
		return asStrings(value)?.flatMap((option) => {
			const details = PARTICIPANT_TYPE_DETAILS[option];

			return details
				? details.map((detail) => `${option}_${detail}`)
				: auditedTypesOf(option);
		});
	}

	if (tab !== DealEventLogTab.CAPITAL_EVENTS) {
		return undefined;
	}

	return asStrings(value)?.flatMap(auditedTypesOf);
};

export const getDealEventLogQuery = (
	tab: DealEventLogTab,
	filters: ColumnFiltersState = []
): DealEventLogQuery => {
	const type = readFilter(filters, DealEventLogColumn.TYPE);
	const entity = readFilter(filters, DealEventLogColumn.ENTITY);
	const { from, to } = toDateRange(readFilter(filters, DealEventLogColumn.OCCURRED_AT));
	const selectedCategories = asEnums(Object.values(DealEventCategory), type);

	return {
		categories:
			tab === DealEventLogTab.ALL_EVENTS
				? selectedCategories
				: DEAL_EVENT_LOG_TAB_CATEGORIES[tab],
		types: toTypes(tab, type),
		statuses: asEnums(
			Object.values(DealEventStatus),
			asOneOrFirst(readFilter(filters, DealEventLogColumn.STATUS))
		),
		recipientEmails: tab === DealEventLogTab.MESSAGING ? asOne(entity) : undefined,
		securityIds: tab === DealEventLogTab.CAPITAL_EVENTS ? asStrings(entity) : undefined,
		investorIds:
			tab === DealEventLogTab.INVESTOR_ACTIVITY ? asParticipantIds(entity) : undefined,
		targetUserIds: tab === DealEventLogTab.TEAM_AND_ACCESS ? asOne(entity) : undefined,
		actorUserId: asText(readFilter(filters, DealEventLogColumn.ACTOR)),
		from,
		to,
	};
};
